// The whole signup form, exactly as the spec describes it: company name, your
// name, email, password. No card, no sales call, no implementation fee.
import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ApiError, api } from '../api';
import { useSession } from '../session';
import { AuthCard, Banner, Field, PasswordField } from '../ui';

/**
 * The browser's own timezone, or undefined when it will not say. Wrapped in a
 * try/catch because an old or locked-down browser can throw here, and a signup
 * must never fail over a nicety — undefined simply means the server's
 * Australia/Sydney default, correctable later on Settings → Company details.
 */
function detectTimezone(): string | undefined {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || undefined;
  } catch {
    return undefined;
  }
}

export function SignupPage() {
  const navigate = useNavigate();
  const { refresh } = useSession();

  const [companyName, setCompanyName] = useState('');
  const [abn, setAbn] = useState('');
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [fields, setFields] = useState<Record<string, string>>({});
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  // While signups are gated closed (SIGNUPS_OPEN, production's pre-launch
  // state) the polite refusal used to arrive only as a 403 — after someone
  // had typed in all four fields. Ask up front instead and show the message
  // in place of the form. A failed check fails OPEN: once signups are open a
  // flaky network must not lock the door, and the post-submit 403 remains
  // the backstop.
  const [gate, setGate] = useState<{ open: boolean; message?: string } | null>(null);
  useEffect(() => {
    let cancelled = false;
    api
      .signupStatus()
      .then((status) => {
        if (!cancelled) setGate({ open: status.signupsOpen, message: status.message });
      })
      .catch(() => {
        if (!cancelled) setGate({ open: true });
      });
    return () => {
      cancelled = true;
    };
  }, []);

  async function submit(event: React.FormEvent) {
    event.preventDefault();
    setError(null);
    setFields({});
    // Checked HERE and never sent: the server has no confirmPassword field,
    // because the confirmation is protection against a typo in the browser,
    // not a fact about the account. (Nathan, launch day 2026-09-14 — he
    // signed up blind with no confirm, no reveal, and no stated rules.)
    if (confirmPassword !== password) {
      setFields({ confirmPassword: 'This does not match the password above.' });
      return;
    }
    setBusy(true);
    try {
      // Left out entirely when blank rather than sent as '' — the field is
      // optional and "not given" is a real answer, not an empty string.
      //
      // The timezone is the browser's own, detected rather than asked for
      // (round 30): a Perth signup lands as Perth without a question nobody
      // at a signup form wants to answer. Wrong-or-missing falls back to the
      // server's Sydney default, and Settings → Company details is the fix.
      const timezone = detectTimezone();
      await api.signup({
        companyName,
        name,
        email,
        password,
        ...(abn ? { abn } : {}),
        ...(timezone ? { timezone } : {}),
      });
      await refresh();
      navigate('/app', { replace: true });
    } catch (err) {
      if (err instanceof ApiError) {
        setError(err.message);
        setFields(err.fields);
      } else {
        setError('Something went wrong. Try again.');
      }
    } finally {
      setBusy(false);
    }
  }

  // Still asking whether signup is open: the card alone, for the few
  // milliseconds a same-origin round trip takes. No skeleton needed.
  if (gate === null) {
    return <AuthCard title="Start your 14-day trial">{null}</AuthCard>;
  }

  if (!gate.open) {
    return (
      <AuthCard title="Start your 14-day trial">
        <p
          className="banner-info mb-4"
          role="status"
        >
          {gate.message ?? 'Dayboard is not taking signups just yet. Check back soon.'}
        </p>
        <p className="footnote">
          Already have an account? <Link to="/login">Sign in</Link>
        </p>
      </AuthCard>
    );
  }

  return (
    <AuthCard title="Start your 14-day trial">
      {error && <Banner kind="error">{error}</Banner>}
      <form onSubmit={submit} noValidate>
        <Field
          label="Company name"
          name="companyName"
          value={companyName}
          onChange={setCompanyName}
          error={fields.companyName}
          autoComplete="organization"
        />
        <Field
          label="ABN (optional)"
          name="abn"
          value={abn}
          onChange={setAbn}
          error={fields.abn}
          required={false}
          hint="If you give it, it appears on your tax invoices. You can add it later."
          autoComplete="off"
          autoCapitalize="none"
          spellCheck={false}
          inputMode="numeric"
        />
        <Field
          label="Your name"
          name="name"
          value={name}
          onChange={setName}
          error={fields.name}
          autoComplete="name"
        />
        <Field
          label="Email"
          name="email"
          type="email"
          value={email}
          onChange={setEmail}
          error={fields.email}
          autoComplete="email"
          autoCapitalize="none"
          spellCheck={false}
        />
        <PasswordField
          label="Password"
          name="password"
          value={password}
          onChange={setPassword}
          error={fields.password}
          hint="At least 10 characters: any letters, numbers or symbols. A few words you'll remember works well."
        />
        <PasswordField
          label="Confirm password"
          name="confirmPassword"
          value={confirmPassword}
          onChange={setConfirmPassword}
          error={fields.confirmPassword}
          enterKeyHint="go"
        />
        <button className="btn-primary full" type="submit" disabled={busy}>
          {busy ? 'Creating your account…' : 'Create account'}
        </button>
      </form>
      <p className="footnote">
        No card required. Cancel and export everything whenever you like.
      </p>
      <p className="footnote">
        By creating an account you agree to the{' '}
        <a href="https://dayboard.com.au/terms.html" target="_blank" rel="noreferrer">
          terms of service
        </a>{' '}
        on your company's behalf. Your data stays in Australia and is never
        sold. See the{' '}
        <a href="https://dayboard.com.au/privacy.html" target="_blank" rel="noreferrer">
          privacy policy
        </a>
        .
      </p>
      <p className="footnote">
        Already have an account? <Link to="/login">Sign in</Link>
      </p>
    </AuthCard>
  );
}
